/**
 * Status-Ableitung pro POSTEN (ScheduledPayment) für die Mollie-Posten-Tabelle.
 *
 * Hintergrund (B-428): Die Section zeigte bis dato nur EINEN Zahlungsversuch —
 * den jüngsten `isCurrentAttempt=true`. Bei mehreren Raten (z.B. Monatlich →
 * 2 ScheduledPayments à 175 €, jede mit eigenem aktuellen Attempt) sah der
 * Admin nur eine Rate und damit einen falschen Betrag. Die Section rendert
 * jetzt eine Zeile je Posten; diese Funktion liefert den Status DIESER Zeile.
 *
 * Reine Funktion (testbar, keine React-/Apollo-Abhängigkeit).
 *
 * Node 14 / TypeScript 4.7 kompatibel (kein `??=`, kein `Array.at`).
 */

import moment from "moment";
import {getDisplayMollieStatus, getMollieFlowKind} from "./mollieFlowKind";
import {formatMollieBanktransferLastValidDay} from "../../../../../../shared/lib/mollieBanktransferDates";
import {getMollieStatusTagDescriptor} from "./mollieStatusTagColor";
import {computeRefundSummary, refundStateFromSummary} from "./refundSummary";
import {isActiveState} from "./isActiveState";

/**
 * Nicht-terminale (Live-)Status, die bei stornierter Buchung als "abgebrochen"
 * dargestellt werden — der Attempt ist dann faktisch nicht mehr aktiv.
 * (Übernommen aus der früheren `AttemptTimelineCard`, B-6967.)
 */
export const NON_TERMINAL_ATTEMPT_STATUSES: ReadonlyArray<string> = [
    "open",
    "pending",
    "authorized",
];

export interface PostenAttemptShape {
    id: string;
    status: string;
    mollieMethod?: string | null;
    amount?: number | null;
    linkExpiresAt?: string | Date | null;
    molliePaymentLinkId?: string | null;
    paidAt?: string | Date | null;
    createDate?: string | Date | null;
    refunds?: ReadonlyArray<{amount?: number | null; status?: string | null}> | null;
}

export interface PostenScheduledPaymentShape {
    id: string;
    paymentAmount?: number | null;
    refundedAmount?: number | null;
    paymentDone?: string | Date | null;
    type?: string | null;
    /**
     * Zahlungsweg des Postens (`ScheduledPayment.paymentProvider`). Nur
     * `"Mollie"`-Posten koennen einen Mollie-Zahlungslink bekommen — SEPA-,
     * Bar- oder Rechnungsposten duerfen den Button NICHT anbieten.
     */
    paymentProvider?: string | null;
}

/** `PaymentProvider.Mollie` als String (Helper bleibt enum-frei testbar). */
const PAYMENT_PROVIDER_MOLLIE = "Mollie";
/** `ScheduledPaymentType.Discount` — Gutschriften werden nie eingezogen. */
const SCHEDULED_PAYMENT_TYPE_DISCOUNT = "Discount";

/**
 * Primäraktion der zugeklappten Zeile. Genau EINE pro Zeile (Sketch final-b):
 *   refund      → Erstatten (öffnet den bestehenden Refund-Flow der ActionBar)
 *   resend      → Link erneut senden
 *   refresh     → Status aktualisieren
 *   newAttempt  → Neuer Zahlungslink (Attempt terminal)
 *   createLink  → Zahlungslink erstellen (noch gar kein Attempt)
 *   none        → keine Aktion (z.B. voll erstattet, stornierte Buchung)
 */
export type PostenPrimaryAction =
    | "refund"
    | "resend"
    | "refresh"
    | "newAttempt"
    | "createLink"
    | "none";

export interface PostenStatus {
    /** Grob-Kategorie — für KPI-Zählung und Tests. */
    kind:
        | "paid"
        | "refundedPartial"
        | "refundedFull"
        | "open"
        | "pending"
        | "terminal"
        | "canceled"
        | "noAttempt";
    label: string;
    /** AntD-Tag-Color. */
    color: string;
    /** Zweitzeile unter dem Tag (z.B. "gültig bis …", "2 Versuche"). */
    secondaryLine: string | null;
    /** true, sobald der Posten als bezahlt gilt (`paymentDone` gesetzt). */
    isPaid: boolean;
    /** true bei laufendem Mollie-Status (open/pending/authorized) → Polling-Hint. */
    isActive: boolean;
    primaryAction: PostenPrimaryAction;
    /** Mollie-Status nach Display-Normalisierung (leer, wenn kein Attempt). */
    displayStatus: string | null;
}

export interface DerivePostenStatusArgs {
    scheduledPayment: PostenScheduledPaymentShape;
    /** Aktueller Attempt DIESES Postens (`isCurrentAttempt`), sonst null. */
    currentAttempt?: PostenAttemptShape | null;
    /** Anzahl aller Attempts dieses Postens (für "n Versuche"-Zweitzeile). */
    attemptCount?: number;
    /** Buchung storniert → nicht-terminale Status werden "abgebrochen". */
    isBookingCanceled?: boolean;
    /**
     * Wartelistenplatz → keine Zahlungsaufforderung (Incident 18.08.2026).
     * Primäraktionen createLink/newAttempt bleiben aus; Statusanzeige unverändert.
     */
    isWaitlistBooking?: boolean;
    /**
     * true bei Sammel-Link-Zeilen: der Attempt gehört dem ganzen Bündel, seine
     * `refunds` sind bündelweit gemischt (Field-Resolver). Die Erstattungslage
     * dieser Zeile kommt dann aus `scheduledPayment.refundedAmount`.
     */
    refundsFromScheduledPayment?: boolean;
    now?: Date;
}

function formatShortDate(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const parsed = moment(value);
    if (!parsed.isValid()) return null;
    return parsed.format("DD.MM.");
}

function formatDateTime(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const parsed = moment(value);
    if (!parsed.isValid()) return null;
    return parsed.format("DD.MM. HH:mm");
}

/** Bezahlter Posten → Tag-Variante je nach Erstattungslage. */
function derivePaidStatus(
    sp: PostenScheduledPaymentShape,
    currentAttempt: PostenAttemptShape | null,
    refundsFromScheduledPayment?: boolean,
): PostenStatus {
    const base = {
        isPaid: true,
        isActive: false,
        secondaryLine: null as string | null,
        displayStatus: currentAttempt ? currentAttempt.status : null,
    };

    // Refund-Lage bevorzugt aus dem Attempt (Status-gefiltert, identisch zur
    // ActionBar). Ohne Attempt bleibt nur `refundedAmount` am Posten.
    if (currentAttempt && !refundsFromScheduledPayment) {
        const summary = computeRefundSummary({
            status: "paid",
            amount: currentAttempt.amount != null ? currentAttempt.amount : sp.paymentAmount,
            refunds: currentAttempt.refunds,
        });
        const refundState = refundStateFromSummary(summary);
        if (refundState === "full") {
            return {...base, kind: "refundedFull", label: "Erstattet", color: "default", primaryAction: "none"};
        }
        if (refundState === "partial") {
            return {
                ...base,
                kind: "refundedPartial",
                label: "Teilw. erstattet",
                color: "warning",
                primaryAction: "refund",
            };
        }
    } else {
        const refunded = Number(sp.refundedAmount || 0);
        const amount = Number(sp.paymentAmount || 0);
        if (refunded > 0) {
            const isFull = amount > 0 && refunded >= amount - 0.001;
            return {
                ...base,
                kind: isFull ? "refundedFull" : "refundedPartial",
                label: isFull ? "Erstattet" : "Teilw. erstattet",
                color: isFull ? "default" : "warning",
                // Anker eines Bündels kann weiter erstatten (über den Bündel-Attempt).
                primaryAction: currentAttempt && !isFull ? "refund" : "none",
            };
        }
    }

    const paidDate = formatShortDate(sp.paymentDone);

    return {
        ...base,
        kind: "paid",
        label: paidDate ? `Bezahlt ${paidDate}` : "Bezahlt",
        color: "success",
        // Ohne Mollie-Attempt gibt es nichts, was via Mollie erstattbar wäre.
        primaryAction: currentAttempt ? "refund" : "none",
    };
}

/**
 * derivePostenStatus — Status + Primäraktion einer Posten-Zeile.
 *
 * Reihenfolge der Regeln:
 *   1. `paymentDone` gesetzt → bezahlt / (teilweise) erstattet.
 *   2. aktueller Attempt → Mollie-Status (mit banktransfer-Normalisierung).
 *      Bei stornierter Buchung werden nicht-terminale Status "abgebrochen".
 *   3. kein Attempt → "Kein Link" (nur Mollie-Posten bekommen hier die
 *      Aktion "Zahlungslink erstellen"; Gutschriften und Nicht-Mollie-Posten
 *      bleiben ohne Aktion).
 */
export function derivePostenStatus(args: DerivePostenStatusArgs): PostenStatus {
    const {scheduledPayment: sp, isBookingCanceled} = args;
    const currentAttempt = args.currentAttempt || null;
    const attemptCount = args.attemptCount != null ? args.attemptCount : currentAttempt ? 1 : 0;
    const now = args.now || new Date();
    // Storno und Warteliste: keine neuen Zahlungsaufforderungen (B-6967 / Incident 18.08.2026).
    const blockPaymentRequest = Boolean(isBookingCanceled) || Boolean(args.isWaitlistBooking);

    // ── 1. Bezahlt ───────────────────────────────────────────────────────
    if (sp.paymentDone) {
        return derivePaidStatus(sp, currentAttempt, args.refundsFromScheduledPayment);
    }

    // ── 3. Kein Attempt ──────────────────────────────────────────────────
    if (!currentAttempt) {
        const base = {
            kind: "noAttempt" as const,
            color: "default",
            secondaryLine: null,
            isPaid: false,
            isActive: false,
            displayStatus: null,
        };

        // Gutschriften werden verrechnet, nicht eingezogen — nie ein Zahlungslink.
        if (sp.type === SCHEDULED_PAYMENT_TYPE_DISCOUNT) {
            return {...base, label: "Gutschrift", primaryAction: "none"};
        }

        // Nur Mollie-Posten koennen einen Mollie-Zahlungslink bekommen; SEPA-,
        // Bar- und Rechnungsposten werden ausserhalb von Mollie ausgeglichen.
        if (sp.paymentProvider !== PAYMENT_PROVIDER_MOLLIE) {
            return {...base, label: "Offen (manuell)", primaryAction: "none"};
        }

        return {
            ...base,
            label: "Kein Link",
            // Bei stornierter Buchung oder Warteliste keine neuen Zahlungsaufforderungen
            // (B-6967 / Incident 18.08.2026).
            primaryAction: blockPaymentRequest ? "none" : "createLink",
        };
    }

    // ── 2. Aktueller Attempt ─────────────────────────────────────────────
    const flowKind = getMollieFlowKind(currentAttempt.mollieMethod);
    const rawDisplayStatus = getDisplayMollieStatus(
        currentAttempt.status,
        currentAttempt.mollieMethod,
        currentAttempt.linkExpiresAt,
        now,
    );
    const isCanceledDisplay =
        Boolean(isBookingCanceled) &&
        NON_TERMINAL_ATTEMPT_STATUSES.indexOf(rawDisplayStatus) !== -1;

    if (isCanceledDisplay) {
        return {
            kind: "canceled",
            label: "abgebrochen",
            color: "default",
            secondaryLine: null,
            isPaid: false,
            isActive: false,
            primaryAction: "none",
            displayStatus: "canceled",
        };
    }

    const attemptsLine = attemptCount > 1 ? `${attemptCount} Versuche` : null;

    switch (rawDisplayStatus) {
        case "open": {
            // Direkte Überweisung: letzter gültiger Tag ohne Uhrzeit, wie im Postendetail
            // und auf der Rechnung (#1036). Zahlungslinks behalten Datum und Uhrzeit.
            const isDirectBanktransfer = flowKind === "banktransfer" && !currentAttempt.molliePaymentLinkId;
            const validUntil =
                isDirectBanktransfer && currentAttempt.linkExpiresAt && moment(currentAttempt.linkExpiresAt).isValid()
                    ? formatMollieBanktransferLastValidDay(currentAttempt.linkExpiresAt, "DD.MM.")
                    : formatDateTime(currentAttempt.linkExpiresAt);

            return {
                kind: "open",
                label: flowKind === "banktransfer" ? "Wartet auf Überweisung" : "Link offen",
                color: "processing",
                secondaryLine: validUntil ? `gültig bis ${validUntil}` : attemptsLine,
                isPaid: false,
                isActive: true,
                primaryAction: "resend",
                displayStatus: rawDisplayStatus,
            };
        }
        case "pending":
        case "authorized":
            return {
                kind: "pending",
                label: "Ausstehend",
                color: "processing",
                secondaryLine: attemptsLine,
                isPaid: false,
                isActive: true,
                primaryAction: "refresh",
                displayStatus: rawDisplayStatus,
            };
        case "expired":
        case "failed":
        case "canceled":
        case "superseded": {
            const descriptor = getMollieStatusTagDescriptor(rawDisplayStatus, "none", flowKind);

            return {
                kind: "terminal",
                label: descriptor.label,
                color: descriptor.color,
                secondaryLine: attemptsLine,
                isPaid: false,
                isActive: false,
                // Neuer Link nur für einbringbare Mollie-Posten: nach einem Wechsel
                // der Zahlweise auf Manuell behält ein Posten seinen letzten
                // (terminalen) Attempt — ein Retry würde ihn still auf Mollie
                // zurücksetzen. Gutschriften werden nie eingezogen.
                primaryAction:
                    blockPaymentRequest || sp.type === SCHEDULED_PAYMENT_TYPE_DISCOUNT || sp.paymentProvider !== PAYMENT_PROVIDER_MOLLIE
                        ? "none"
                        : "newAttempt",
                displayStatus: rawDisplayStatus,
            };
        }
        default: {
            // Unbekannter/leerer Status (z.B. vor dem ersten Mollie-Webhook).
            const descriptor = getMollieStatusTagDescriptor(rawDisplayStatus, "none", flowKind);

            return {
                kind: "terminal",
                label: descriptor.label,
                color: descriptor.color,
                secondaryLine: attemptsLine,
                isPaid: false,
                isActive: isActiveState(rawDisplayStatus),
                primaryAction: "none",
                displayStatus: rawDisplayStatus,
            };
        }
    }
}
